import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import type { AxiosError } from 'axios';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import {
  resolveSuggestedTier,
  resolveTierFitWarningDay,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { LEAD_AGENT_PAYWALL_EVENTS } from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import { ePlanFrequency } from 'src/providers/SubscriptionProvider/Subscription.enums';
import type { tLeadAgentSelectableTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import CheckoutCopyGate from '../CheckoutCopyGate.component';
import { formatPaymentDate } from '../../LeadAgentAcceptanceBanner.functions';
import {
  buildUpgradeRenewalBreakdown,
  formatTierPrice,
  parsePlanFrequency,
  readLeadAgentErrorCode,
  renewalPeriodKey,
  formatZeroToday,
  resolveDeferredFirstChargeAt,
} from '../acceptanceModals.functions';
import { formatPrice } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import {
  resolveBillingErrorMessage,
  type tBillingErrorPayload,
} from 'src/utils/resolveBillingError';
import { useConfirmTierModal } from './ConfirmTierModal.hook';
import type { tConfirmTierModalProps } from './ConfirmTierModal.types';

const NS = 'common/leadAgentAcceptanceModals';
const REACTIVATION_NS = 'common/leadAgentReactivateModal';
// Billing error copy (decline map + actionable fallbacks) lives in this namespace.
const BILLING_NS = 'settings/SubscriptionManager';

/**
 * Lead Agent tier confirmation modal (RET-1230 / C2, Unit 5; RET-1287). Shows
 * the suggested tier up front with "Ver otros planes" to reveal the rest, a
 * renewal breakdown projected by leadbilling, a terms checkbox, and the chosen
 * tier. Before renewal it keeps the deferred PATCH. After renewal it shows the
 * exact reactivation preview and charges that pinned amount (including 3DS).
 * Emits `la_paywall_modal_opened` on mount.
 */
export default function ConfirmTierModal(props: tConfirmTierModalProps) {
  const { onClose, onConfirmed } = props;
  const { t, ready: copyReady } = useTranslation(NS);
  const { t: tReactivation, ready: reactivationCopyReady } =
    useTranslation(REACTIVATION_NS);
  // `ready` guards the lazy-loaded billing namespace: if a decline lands before
  // the JSON is fetched, fall back to the modal's already-loaded copy rather than
  // surfacing a raw i18n key (RET-1372).
  const { t: tBilling, ready: billingReady } = useTranslation(BILLING_NS);
  const { addAlert } = useToastContext();
  const { trackEvent } = useAnalytics();
  const {
    acceptance,
    acceptTier,
    refreshAcceptance,
    markResolved,
    isTrialCappedForOwner,
    isTrialExtendedCappedForOwner,
    trialCapOffer,
  } = useLeadAgentAcceptanceContext();
  const { subscription } = useSubscriptionContext();

  const suggestedTier = resolveSuggestedTier(acceptance?.suggestedTier);
  const workspaceId = acceptance?.workspaceId ?? '';
  /*
   * Two different frequencies on purpose.
   *
   * `frequency` builds the SKU (`LEAD_AGENT_{TIER}_{FREQ}_{CURRENCY}`) and has
   * to come from the subscription, which is what the catalog is keyed on. It
   * fails CLOSED rather than guessing: `currency` travels with it, and an empty
   * currency makes `buildLeadAgentProductCode` return null, so an unloaded
   * subscription yields an empty catalog and the "no catalog" state — not a
   * monthly price wearing a quarterly workspace's name.
   *
   * `displayFrequency` is what LABELS the money, and it prefers the
   * PROJECTION's own frequency — the same source as the amounts it labels.
   * `ReactivateLeadAgentModal` learned this first: reading the subscription
   * alone let an annual total render with a "/mes" suffix while the summary was
   * still loading. Same trap here, now that the total carries a period word:
   * "Total mensual" over an annual figure is worse than no word at all.
   */
  const frequency = subscription?.frequency ?? ePlanFrequency.MONTHLY;
  const currency = subscription?.currency ?? '';

  const confirmHook = useConfirmTierModal({
    workspaceId,
    suggestedTier,
    frequency,
    currency,
    nextPaymentAt: resolveDeferredFirstChargeAt(acceptance, frequency),
    origin: acceptance?.origin ?? null,
    phase: acceptance?.phase ?? null,
  });
  const {
    catalog,
    projection,
    selectedTier,
    selectTier,
    retry,
    startChargeNow,
    confirmChargeNow,
    retryChargeNow,
    isChargeNow,
    reactivationPreview,
    chargeView,
  } = confirmHook;

  const [showAllTiers, setShowAllTiers] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Inline terms-consent error: the submit stays enabled (gated only on data
  // readiness / in-flight), and clicking it without accepting flips this true to
  // surface a red checkbox + helper line — not a disabled button (Fanny, RET-1431).
  const [showTermsError, setShowTermsError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Synchronous in-flight latch. `submitting` is async React state, so two
  // clicks dispatched in the same tick both read the pre-update value and fire
  // two PATCHes (the 2nd returns 409 and toasts a confusing "state changed").
  // This ref flips synchronously inside the handler so the 2nd click bails.
  const inFlightRef = useRef(false);

  // A pending charge is finalized by Stripe webhooks. Reopening this modal must re-read the
  // acceptance row so a completed webhook cannot leave the customer looking at stale pre-charge
  // state for the rest of the session.
  //
  // RET-2329 (KTD3, R5) — at most ONE relecture per OPEN, not per render. The
  // four hosts mount this modal keyed only on a `view` state, without a
  // `key`, so one mount is one open — but a mount can OUTLIVE a workspace
  // switch (the sibling effect right below resets workspace-scoped UI state
  // on `[workspaceId]` instead of assuming a remount, and the trial-cap
  // host's own workspace-change effect only closes the `unlock` view, never
  // `acceptDeferred`). The guard is keyed on the WORKSPACE the last refresh
  // ran for, not a plain boolean: it latches the first time the phase reads
  // `after_renewal` FOR THAT WORKSPACE and blocks every later run for the
  // same one, including a webhook-driven phase flip back to `after_renewal`
  // after leaving it, and a background `refreshAcceptance` (RET-2329/U1)
  // replacing the acceptance object without changing phase or workspace — but
  // re-arms the instant the workspace changes, so the new workspace still
  // gets its mandatory re-read. `handleClose`'s own pending-charge refresh is
  // untouched — that one fires on CLOSE, not on open, and still runs every
  // time.
  const hasRefreshedOnOpenRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      acceptance?.phase === 'after_renewal' &&
      hasRefreshedOnOpenRef.current !== workspaceId
    ) {
      hasRefreshedOnOpenRef.current = workspaceId;
      void refreshAcceptance();
    }
  }, [acceptance?.phase, workspaceId, refreshAcceptance]);

  useEffect(() => {
    setShowAllTiers(false);
    setAcceptedTerms(false);
    setShowTermsError(false);
  }, [workspaceId]);

  // Emit `la_paywall_modal_opened` once on mount (entry_point: banner).
  useEffect(() => {
    const props: Record<string, string> = {
      suggested_tier: suggestedTier.toLowerCase(),
      entry_point: 'banner',
    };
    if (acceptance?.workspaceBucket) {
      props.workspace_bucket = acceptance.workspaceBucket;
    }
    trackEvent(LEAD_AGENT_PAYWALL_EVENTS.MODAL_OPENED, props);
    // Mount-only: the modal mounts fresh each open, so a single emit per open
    // is exactly one `modal_opened`. Deps intentionally empty.
  }, []);

  const chargeDate = useMemo(
    () => formatPaymentDate(acceptance?.nextPaymentAt ?? null),
    [acceptance?.nextPaymentAt],
  );

  /*
   * Reaching this modal from the trial cap means the bot is ALREADY silent, and
   * pre-accepting does not wake it — it stays paused until the trial ends. That
   * is the one thing the owner most needs to know before consenting, and it was
   * missing (Fanny, 2026-09-01, against the prototype).
   *
   * Scoped to the capped states ON PURPOSE. Reached from the plain offer the
   * bot is answering normally, and telling that owner their bot "sigue en
   * pausa" would be a lie that costs us the sale.
   *
   * The date is the TRIAL END from the cap offer, not `nextPaymentAt`: those
   * are different moments, and this sentence is about when the bot speaks
   * again, not about when money moves.
   */
  const isBotPausedByCap =
    isTrialCappedForOwner || isTrialExtendedCappedForOwner;
  const trialEndDate = useMemo(
    () => formatPaymentDate(trialCapOffer?.trialEnd ?? null),
    [trialCapOffer?.trialEnd],
  );

  /*
   * RET-2118 — the client picks a tier BELOW our own recommendation and never
   * sees why: 13 paid workspaces lived capped 12-27 days of the cycle without
   * widening. The backend (`LeadAgentTierProjector`, MLA-05) already computes,
   * per tier, the raw-consumption day it exhausts (`acceptance.tierFit`); this
   * only READS that day — no projection, no tokens, no caps are recomputed
   * here (docs/loop/RET-2118-ESTADO.md). Null (older BFF / parser fail-closed,
   * or the selection is at/above the recommendation) paints nothing — exactly
   * today's behavior.
   */
  const tierFitWarningDay = resolveTierFitWarningDay({
    selectedTier,
    suggestedTier,
    tierFit: acceptance?.tierFit,
  });

  /*
   * The DEDUPING builder, and the Lead Agent line NAMED through i18n.
   *
   * Both fixes answer the same screenshot (Fanny, 2026-09-01): reached from the
   * trial-cap "desbloquear al finalizar mi prueba" option, this modal listed the
   * add-on twice — once as the catalog's "Lead Agent Growth" and again as the
   * raw enum "Lead Agent GROWTH" — and totalled both. The endpoint composes the
   * recurring lines and then ADDS the requested tier without deduping, so any
   * payload that already lists the add-on counts it twice; dropping the listed
   * line is a no-op when there is none, which is the shape a workspace still in
   * its trial actually sends.
   *
   * The label is resolved here because this component has the `t`: the builder
   * is pure and was falling back to the billing enum, which is how a customer
   * ended up reading GROWTH in caps.
   */
  const breakdown = useMemo(() => {
    if (projection.status !== 'ready') return null;
    const tier = projection.data.leadAgentLine.tier;
    return buildUpgradeRenewalBreakdown(projection.data, {
      leadAgentLabel:
        `Lead Agent ${t(`tier.${tier}`, { defaultValue: '' })}`.trim(),
    });
  }, [projection, t]);

  const displayFrequency =
    parsePlanFrequency(
      projection.status === 'ready' ? projection.data.frequency : null,
    ) ??
    subscription?.frequency ??
    ePlanFrequency.MONTHLY;
  // Null for a non-billable periodicity → the total degrades to its plain
  // wording rather than printing "Total  de tu renovación".
  const renewalPeriodKeyValue = renewalPeriodKey(displayFrequency);
  const renewalPeriodLabel = renewalPeriodKeyValue
    ? t(`confirm.renewalPeriod.${renewalPeriodKeyValue}`)
    : '';

  const projectionCurrency =
    projection.status === 'ready' ? projection.data.currency : currency;

  const projectionReady = projection.status === 'ready';
  const immediatePreviewReady =
    !isChargeNow || reactivationPreview.status === 'ready';
  // Enabled re: terms (Fanny, RET-1431): only hard-disabled while the projection
  // (the amount) isn't ready or a submit is in flight. Terms consent is enforced
  // at click time via the inline error, not by disabling the button.
  const isProcessing = submitting || chargeView.status === 'processing';
  const confirmDisabled =
    !projectionReady ||
    !immediatePreviewReady ||
    isProcessing ||
    chargeView.status === 'pending';
  // `confirmDisabled` gates BOTH the submit button and the terms checkbox — terms
  // consent is never part of it (Fanny, RET-1431). The inline terms error shows ONLY
  // while the checkbox is actionable, so it never lingers beneath a disabled checkbox
  // — e.g. after the owner clicks submit unchecked and then hits the projection retry.
  const showTermsErrorVisible = showTermsError && !confirmDisabled;

  function handleSelectTier(tier: tLeadAgentSelectableTier) {
    if (tier === selectedTier) return;
    setAcceptedTerms(false);
    setShowTermsError(false);
    selectTier(tier);
  }

  function handleClose() {
    if (isProcessing) {
      addAlert({
        type: eAlertType.INFO,
        message: tReactivation('processing.wait'),
      });
      return;
    }
    if (chargeView.status === 'pending') {
      void refreshAcceptance();
    }
    onClose();
  }

  async function handleConfirm() {
    // Synchronous double-click guard (see `inFlightRef`): bail before any state
    // dispatch so a same-tick second click cannot start a second PATCH.
    if (inFlightRef.current) return;
    // Capture before the await — context state can change mid-flight
    // (docs/solutions/logic-errors/websocket-race-overwrites-guard-state-during-await.md).
    const tierToConfirm = selectedTier;
    // Loading/in-flight safety net (button is disabled in these states).
    if (confirmDisabled) return;
    // Terms consent is enforced here (button stays enabled): surface the inline
    // error instead of pre-accepting.
    if (!acceptedTerms) {
      setShowTermsError(true);
      return;
    }
    inFlightRef.current = true;
    setSubmitting(true);
    try {
      if (isChargeNow) {
        const result = await confirmChargeNow();
        if (result?.status !== 'ok' || !acceptance) return;
        markResolved(tierToConfirm);
        onConfirmed({
          acceptance,
          tier: tierToConfirm,
          renewalTotal: breakdown?.renewalTotal ?? null,
          // `nextPaymentAt` is the renewal that already passed on this route.
          // The success modal labels this field "Next renewal", so use the
          // projection's new renewal date instead of displaying a stale date.
          chargeDate: breakdown?.renewalDate ?? null,
        });
        return;
      }

      const updated = await acceptTier(tierToConfirm);
      markResolved(tierToConfirm);
      onConfirmed({
        acceptance: updated,
        tier: tierToConfirm,
        renewalTotal: breakdown?.renewalTotal ?? null,
        chargeDate,
      });
    } catch (error) {
      if (readLeadAgentErrorCode(error) === 'use_reactivation') {
        // The local phase went stale between rendering and /accept. Move onto
        // the money path, fetch the exact amount, and require a fresh confirm.
        setAcceptedTerms(false);
        setShowTermsError(false);
        startChargeNow();
        return;
      }
      const axiosError = error as AxiosError | undefined;
      const status = axiosError?.response?.status;
      if (status === 409) {
        // Illegal transition: the row changed in another session. The banner
        // is stale — resolve it and close.
        addAlert({
          type: eAlertType.ERROR,
          message: t('errors.stateChanged'),
        });
        markResolved();
        onClose();
        return;
      }
      // RET-1372: a bank decline (422 + structured payload) must surface an
      // actionable message, not a generic "algo salió mal". The resolver reads
      // the forwarded billing body; anything unrecognized still falls back to a
      // generic message (never a raw code).
      addAlert({
        type: eAlertType.ERROR,
        message: billingReady
          ? resolveBillingErrorMessage(
              axiosError?.response?.data as tBillingErrorPayload,
              tBilling,
            )
          : t('errors.unexpected'),
      });
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  }

  const readyTiers = catalog.status === 'ready' ? catalog.tierProducts : [];
  const todayAmount =
    isChargeNow && reactivationPreview.status === 'ready'
      ? formatPrice({
          price: reactivationPreview.data.amountCents,
          currency: reactivationPreview.data.currency,
        })
      : formatZeroToday(projectionCurrency);
  // Suggested tier first, then the rest in catalog order.
  const orderedTiers = [
    ...readyTiers.filter((entry) => entry.tier === suggestedTier),
    ...readyTiers.filter((entry) => entry.tier !== suggestedTier),
  ];

  // Sin la copy de este modal no hay nada que leer: `t()` devolvería
  // llaves crudas junto a montos correctos. Ver CheckoutCopyGate.
  const pendingCopyReady =
    chargeView.status !== 'pending' || reactivationCopyReady;
  if (!copyReady || !pendingCopyReady) {
    return (
      <CheckoutCopyGate
        className="LeadAgentConfirmTierModal rounded-lg p-0 min-w-[34rem] max-w-[34rem]"
        onClose={handleClose}
      />
    );
  }

  return (
    <Modal.Legacy
      open
      onClickBackdrop={handleClose}
      className="LeadAgentConfirmTierModal rounded-lg p-0 min-w-[34rem] max-w-[34rem]"
    >
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="text-lg font-semibold">{t('confirm.title')}</h3>
        <XMarkIcon
          onClick={handleClose}
          className="size-6 cursor-pointer text-neutral-500"
          data-testid="confirm-tier-modal-close"
        />
      </div>

      <div className="p-4 flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-neutral-600">
            {t('confirm.yourPlan')}
          </span>
          {readyTiers.length > 1 ? (
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-neutral-500 transition-colors hover:text-neutral-700"
              onClick={() => setShowAllTiers((prev) => !prev)}
              data-testid="confirm-tier-modal-toggle"
            >
              <ChevronDownIcon
                className={classnames('size-4 transition-transform', {
                  'rotate-180': showAllTiers,
                })}
                aria-hidden="true"
              />
              {showAllTiers
                ? t('confirm.hidePlans')
                : t('confirm.showOtherPlans')}
            </button>
          ) : null}
        </div>

        {catalog.status === 'loading' ? (
          <p
            className="text-sm text-neutral-500"
            data-testid="confirm-tier-modal-catalog-loading"
          >
            {t('confirm.loadingCatalog')}
          </p>
        ) : null}

        {catalog.status === 'error' || orderedTiers.length === 0 ? (
          <div
            className="flex flex-col gap-2 rounded border border-error/30 bg-error/5 p-3"
            data-testid="confirm-tier-modal-catalog-error"
          >
            <p className="text-sm text-error">{t('errors.catalog')}</p>
            <button
              type="button"
              className="self-start text-sm text-primary underline"
              onClick={retry}
              data-testid="confirm-tier-modal-catalog-retry"
            >
              {t('errors.retry')}
            </button>
          </div>
        ) : (
          <div
            className="flex flex-col gap-2"
            data-testid="confirm-tier-modal-plan-list"
          >
            {orderedTiers.map((entry) => {
              const isSelected = entry.tier === selectedTier;
              const isSuggested = entry.tier === suggestedTier;
              const isHidden = !isSuggested && !showAllTiers;
              if (isHidden) return null;
              return (
                <label
                  key={entry.tier}
                  className={classnames(
                    'flex items-center gap-3 rounded border p-3 cursor-pointer',
                    {
                      'border-primary bg-primary/5': isSelected,
                      'border-base-300': !isSelected,
                    },
                  )}
                  data-testid={`confirm-tier-option-${entry.tier}`}
                >
                  <input
                    type="radio"
                    name="lead-agent-tier"
                    className="radio radio-primary radio-sm"
                    checked={isSelected}
                    onChange={() => handleSelectTier(entry.tier)}
                    data-testid={`confirm-tier-radio-${entry.tier}`}
                  />
                  <span className="flex-1 flex flex-col">
                    <span className="text-sm font-medium flex items-center gap-2">
                      {t(`tier.${entry.tier}`)}
                      {isSuggested ? (
                        <span className="badge badge-sm badge-primary">
                          {t('confirm.recommended')}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {t(`confirm.tierDescription.${entry.tier}`)}
                    </span>
                  </span>
                  <span className="text-sm font-semibold">
                    {formatTierPrice(entry.product, frequency)}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {tierFitWarningDay !== null ? (
          <p
            className="text-sm text-warning-on-tint bg-warning-tint-surface border border-warning/40 rounded p-3"
            data-testid="confirm-tier-modal-fit-warning"
          >
            {t('confirm.tierFitWarning', {
              day: tierFitWarningDay,
              /*
               * El tier va por nombre, no como «este plan». En esta pantalla
               * «plan» está sobrecargado: arriba nombra los niveles del Lead
               * Agent y abajo, en el desglose de renovación, nombra el Plan
               * Professional. Nombrar «Starter» no deja lugar a la duda.
               */
              tier: t(`tier.${selectedTier}`),
            })}
          </p>
        ) : null}

        {/* "Hoy" is its OWN card, as the prototype has it, and no longer a row
            folded into the renewal panel. What the customer pays TODAY and what
            they will pay LATER are two different facts, and sharing one frame
            made the $0.00 read as part of the renewal arithmetic instead of as
            the answer to "does clicking this charge me now?". Gated on the
            breakdown so it never claims $0.00 in a currency we have not
            resolved yet. */}
        {breakdown &&
        (!isChargeNow || reactivationPreview.status === 'ready') ? (
          <div
            className="rounded border border-base-300 p-3 flex justify-between text-sm"
            data-testid="confirm-tier-modal-today"
          >
            <span className="flex flex-col">
              <span>{t('confirm.today')}</span>
              <span className="text-xs text-neutral-500">
                {isChargeNow
                  ? t('confirm.chargeToday')
                  : t('confirm.noChargeToday')}
              </span>
            </span>
            <span
              className={classnames('font-semibold', {
                'text-success': !isChargeNow,
              })}
              data-testid="confirm-tier-modal-today-amount"
            >
              {todayAmount}
            </span>
          </div>
        ) : null}

        {isChargeNow && reactivationPreview.status === 'loading' ? (
          <p
            className="text-sm text-neutral-500"
            data-testid="confirm-tier-modal-reactivation-loading"
          >
            {t('confirm.loadingImmediateCharge')}
          </p>
        ) : null}

        {isChargeNow && reactivationPreview.status === 'error' ? (
          <div
            className="flex flex-col gap-2 rounded border border-error/30 bg-error/5 p-3"
            data-testid="confirm-tier-modal-reactivation-preview-error"
            role="alert"
          >
            <p className="text-sm text-error">{t('errors.chargePreview')}</p>
            <button
              type="button"
              className="self-start text-sm text-primary underline"
              onClick={retryChargeNow}
            >
              {t('errors.retry')}
            </button>
          </div>
        ) : null}

        {isChargeNow && chargeView.status === 'error' ? (
          <div
            className="flex flex-col gap-2 rounded border border-error/30 bg-error/5 p-3"
            data-testid="confirm-tier-modal-charge-error"
            role="alert"
          >
            <p className="text-sm text-error">{t('errors.chargeDeclined')}</p>
            <button
              type="button"
              className="self-start text-sm text-primary underline"
              onClick={retryChargeNow}
            >
              {t('errors.retryPayment')}
            </button>
          </div>
        ) : null}

        {isChargeNow && chargeView.status === 'pending' ? (
          <div
            className="flex flex-col gap-1 rounded border border-info/30 bg-info/5 p-3"
            data-testid="confirm-tier-modal-charge-pending"
            role="status"
          >
            <p className="text-sm font-medium">
              {tReactivation(
                chargeView.kind === 'invoice'
                  ? 'success.invoiceSentTitle'
                  : chargeView.kind === 'open_invoice'
                    ? 'success.openInvoiceTitle'
                    : 'success.title',
              )}
            </p>
            <p className="text-sm text-neutral-600">
              {tReactivation(
                chargeView.kind === 'invoice'
                  ? 'success.invoiceSentBody'
                  : chargeView.kind === 'open_invoice'
                    ? 'success.openInvoiceBody'
                    : 'success.processing',
              )}
            </p>
          </div>
        ) : null}

        {/* ---- Renewal breakdown (RET-1287 projection from leadbilling) ---- */}
        <div
          className="rounded border border-base-300 p-3 flex flex-col gap-3"
          data-testid="confirm-tier-modal-billing"
        >
          {projection.status === 'loading' ? (
            <p
              className="text-sm text-neutral-500"
              data-testid="confirm-tier-modal-projection-loading"
            >
              {t('confirm.loadingBilling')}
            </p>
          ) : null}

          {projection.status === 'error' ? (
            <div
              className="flex flex-col gap-2"
              data-testid="confirm-tier-modal-projection-error"
            >
              <p className="text-sm text-error">{t('errors.preview')}</p>
              <button
                type="button"
                className="self-start text-sm text-primary underline"
                onClick={retry}
                data-testid="confirm-tier-modal-projection-retry"
              >
                {t('errors.retry')}
              </button>
            </div>
          ) : null}

          {breakdown ? (
            <>
              {/*
                RET-1290: solo el cargo PUENTE (presale no-mensual) se muestra como
                "primer cargo" propio. Para `full` (mensual / post-launch) el Lead
                Agent se cobra completo en la renovacion y aparece en sus lineas, sin
                seccion puente. El monto es exacto: ya no hay leyenda "estimado".
              */}
              {breakdown.firstCharge.kind === 'bridge' ? (
                <div
                  /* Sin `border-t`: al mover "Hoy" a su propia tarjeta este
                     bloque quedó el PRIMERO del panel, y su separador dibujaba
                     una segunda raya justo bajo el borde del contenedor. El
                     separador que sí hace falta es el de la renovación, abajo. */
                  className="flex flex-col gap-1"
                  data-testid="confirm-tier-modal-first-charge"
                >
                  <div className="flex justify-between text-sm">
                    <span className="flex flex-col">
                      <span>{t('confirm.firstCharge')}</span>
                      {breakdown.firstCharge.date ? (
                        <span className="text-xs text-neutral-500">
                          {breakdown.firstCharge.date}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className="font-semibold"
                      data-testid="confirm-tier-modal-first-charge-amount"
                    >
                      {breakdown.firstCharge.amount}
                    </span>
                  </div>
                  <p
                    className="text-xs text-neutral-500"
                    data-testid="confirm-tier-modal-first-charge-covers"
                  >
                    {t('confirm.firstChargeCovers')}
                  </p>
                </div>
              ) : null}

              <div className="border-t border-base-200 pt-3 flex flex-col gap-1">
                <p className="text-sm font-medium">
                  {breakdown.renewalDate
                    ? t('confirm.nextRenewalDated', {
                        date: breakdown.renewalDate,
                      })
                    : t('confirm.nextRenewal')}
                </p>
                {breakdown.renewalLines.map((line, index) => (
                  <div
                    key={`${line.description}-${index}`}
                    className="flex justify-between text-sm"
                  >
                    <span className="flex items-center gap-2">
                      {line.description}
                      {line.isNew ? (
                        <span className="badge badge-xs badge-success">
                          {t('confirm.new')}
                        </span>
                      ) : null}
                    </span>
                    <span>{line.amount}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-bold border-t border-base-200 pt-2 mt-1">
                  <span>
                    {/* An annual total is five figures; without the period
                        word beside it, it reads as a monthly bill. */}
                    {renewalPeriodLabel
                      ? t('confirm.renewalTotalPeriod', {
                          period: renewalPeriodLabel,
                        })
                      : t('confirm.renewalTotal')}
                  </span>
                  <span data-testid="confirm-tier-modal-renewal-total">
                    {breakdown.renewalTotal}
                  </span>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Sits between the money and the consent because it is the answer to
            "what happens if I tick this": nothing today, and the bot is still
            quiet until the trial ends. Only rendered when both are true — on
            the after_renewal route the renewal already charges immediately
            (MLA-37), so isChargeNow rules this out even with the cap active. */}
        {isBotPausedByCap && !isChargeNow ? (
          <p
            className="text-sm text-neutral-600"
            data-testid="confirm-tier-modal-paused-note"
          >
            {trialEndDate
              ? t('confirm.pausedUntilTrialEnd', { date: trialEndDate })
              : t('confirm.pausedUntilTrialEndNoDate')}
          </p>
        ) : null}

        <label
          className={classnames(
            'flex items-start gap-2 text-sm cursor-pointer',
            { 'text-error': showTermsErrorVisible },
          )}
        >
          <input
            type="checkbox"
            className={classnames('checkbox checkbox-sm mt-0.5', {
              'checkbox-error': showTermsErrorVisible,
            })}
            checked={acceptedTerms}
            onChange={(event) => {
              setAcceptedTerms(event.target.checked);
              if (event.target.checked) setShowTermsError(false);
            }}
            disabled={confirmDisabled}
            data-testid="confirm-tier-modal-terms"
          />
          <span>
            {/* B7 rows carry next_payment_at = null until the A4 batch anchors
                the personal charge date; a billing consent label must never
                render with an empty date. */}
            {isChargeNow
              ? t('confirm.chargeTerms')
              : chargeDate
                ? t('confirm.terms', { date: chargeDate })
                : t('confirm.termsNoDate')}
          </span>
        </label>
        {showTermsErrorVisible ? (
          <p
            className="text-sm text-error"
            data-testid="confirm-tier-modal-terms-error"
          >
            {t('confirm.termsError')}
          </p>
        ) : null}
      </div>

      <div className="LeadAgentConfirmTierModal__Actions p-4 bg-base-200 flex gap-3">
        <Button
          color="primary"
          size="sm"
          className="rounded flex-1"
          onClick={() => {
            void handleConfirm();
          }}
          disabled={confirmDisabled}
          loading={submitting}
          data-testid="confirm-tier-modal-submit"
        >
          {isChargeNow ? t('confirm.submitAndPay') : t('confirm.submit')}
        </Button>
        <Button
          color="ghost"
          size="sm"
          className="rounded"
          onClick={handleClose}
          disabled={isProcessing}
          data-testid="confirm-tier-modal-cancel"
        >
          {t('confirm.cancel')}
        </Button>
      </div>
    </Modal.Legacy>
  );
}
